import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Box,
  Text,
  Grid,
  Heading,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Checkbox,
  useColorModeValue,
  Tag,
  TagLabel,
  IconButton,
} from "@chakra-ui/react";
import {
  CollecticonChevronDownSmall,
  CollecticonChevronUpSmall,
  CollecticonMap,
  CollecticonArrowSemiSpinCcw
} from "@devseed-ui/collecticons-chakra";
import { MapRef } from "react-map-gl";
import { BBox, Feature, Geometry } from "geojson";

import { SceneCard, LoadingSceneCard, Upload } from "./components";
import { FnToggleScenesView, ScenesView } from "./types";

import { Error, Loading, Map, MapGrid, MapScenesLegend, DownloadDetections, DatePicker } from "../../components";
import { MAX_DATE, MIN_DATE } from "../../settings";
import { SceneFeature } from "../../types/scenes";
import { groupByScene, inferenceStatsPerGroup } from "../../utils/inferenceStats";
import { useInferences, useQueryParams, useScenes } from "../../hooks";
import { PANEL_SIZE } from "../../theme";

function Scenes() {
  const mapRef = useRef<MapRef>(null);
  const [view, setView] = useState<ScenesView>(ScenesView.Map);
  const [filterBbox, setFilterBbox] = useState<boolean>(false);
  const {
    selectedDates,
    setSelectedDates,
    bbox,
    setBbox,
  } = useQueryParams();
  const {
    isLoading,
    error,
    scenes,
    refetch
  } = useScenes({selectedDates, bbox, subResource: "user"});
  const { inferences, isLoading: inferenceLoading } = useInferences({
    selectedDates: [MIN_DATE, MAX_DATE],
    bbox: undefined,
    validated: undefined,
    asPoints: true,
    subResource: "user"
  });

  const scenesSorted = useMemo(() => {
    if (!scenes) return;
    return scenes?.features.sort((a, b) => new Date(a.properties.date).valueOf() - new Date(b.properties.date).valueOf());
  }, [scenes]);

  const inferenceStats = useMemo(() => {
    const inferencesByScene = groupByScene(inferences?.features || []);
    return inferenceStatsPerGroup(inferencesByScene);
  }, [inferences]);

  const panelBg = useColorModeValue("base.50", "base.700");
  const checkboxBg = useColorModeValue("base.50", "base.500");
  const borderColor = useColorModeValue("base.100a", "base.500");

  const toggleScenesView: FnToggleScenesView = useCallback(() => {
    if (view === ScenesView.List) {
      setView(ScenesView.Map);
    } else {
      setView(ScenesView.List);
    }
  }, [view]);

  useEffect(() => {
    if (filterBbox) {
      setBbox(mapRef.current?.getBounds().toArray().flat() as BBox);
    } else {
      setBbox(undefined);
    }
  }, [filterBbox, setBbox]);

  const handleBboxChange = useCallback(() => {
    if (filterBbox) {
      setBbox(mapRef.current?.getBounds().toArray().flat() as BBox);
    }
  }, [filterBbox, setBbox]);

  const resetFilters = () => {
    setSelectedDates([MIN_DATE, MAX_DATE]);
    setFilterBbox(false);
    setBbox(undefined);
  };
  const defaultFilterState = filterBbox === false && JSON.stringify(selectedDates) == JSON.stringify([MIN_DATE, MAX_DATE]);
  
  return (
    <Box
      position="relative"
      display="grid"
      gridTemplateColumns={view === ScenesView.Map ? ["1fr", null, `${PANEL_SIZE} 1fr`] : "1fr"}
      gridTemplateRows={view === ScenesView.Map ? ["3fr 1fr", null, "1fr"] : "1fr"}
      overflow="hidden"
      mx={view === ScenesView.Map ? 0 : [0, 0, null, 48]}
    >
      <Grid
        templateRows={view === ScenesView.List ? "max-content max-content max-content 1fr" : "max-content max-content 1fr max-content"}
        p="5"
        gap="5"
        overflow="auto"
        boxShadow={view === ScenesView.Map ? ["0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)", null, "md"] : "none"}
        gridRow={view === ScenesView.Map ? ["2", null, "initial"] : "initial"}
        zIndex="10"
        borderRight={view === ScenesView.Map ? "1px solid" : "none"}
        borderColor={borderColor}
      >
        <Box display="flex" gap="5" alignItems="center">
          <Heading size="md" as="h2" flex="1">
            Uploaded Scenes
          </Heading>
          <Button
            size="sm"
            rightIcon={<CollecticonMap />}
            variant="primary-outline"
            onClick={toggleScenesView}
          >
            {view === ScenesView.Map ? "Hide Map" : "Show Map"}
          </Button>
        </Box>
        <Upload onSubmit={refetch} />
        {view === ScenesView.List && (
          <Box display="flex" justifyContent="space-between">
            <Box flex="1" display="flex" gap="4" alignItems="center">
              <Popover placement="bottom-start">
                {({ isOpen }) => (
                  <>
                    <PopoverTrigger>
                      <Button
                        variant="primary-outline"
                        rightIcon={isOpen ? <CollecticonChevronUpSmall /> : <CollecticonChevronDownSmall />}
                      >
                        Select date
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent py={1} px={1} minWidth="max-content">
                      <PopoverArrow />
                      <PopoverBody>
                        <DatePicker
                          minDate={MIN_DATE}
                          maxDate={MAX_DATE}
                          selectedDates={selectedDates}
                          setSelectedDates={setSelectedDates}
                        />
                      </PopoverBody>
                    </PopoverContent>
                  </>
                )}
              </Popover>
              {(MIN_DATE.valueOf() < selectedDates[0].valueOf() ||  (MAX_DATE.valueOf() - 50000)  > selectedDates[1].valueOf()) && // use a 50 second buffer between repeated Date.now() calls for maxDate
                <Tag colorScheme="primary" overflow="hidden">
                  <TagLabel>{selectedDates[0].toLocaleDateString("en-US")} - {selectedDates[1].toLocaleDateString("en-US")}</TagLabel>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    ml={1}
                    mr={-2}
                    borderRadius="none"
                    icon={<CollecticonArrowSemiSpinCcw size="10px" />}
                    onClick={() => setSelectedDates([MIN_DATE, MAX_DATE])}
                    aria-label="Reset date filter"
                    title="Reset date filter"
                  />
                </Tag>}
            </Box>
            <DownloadDetections
              inferences={inferences}
              fileName={`all-detections-${new Date().toISOString().split(".")[0]}.csv`}
              isLoading={inferenceLoading}
              isDisabled={!inferences?.features.length}
            >
              Download All Inferences
            </DownloadDetections>
          </Box>
        )}
        { isLoading || error ? (
          <>
            { isLoading && 
            <Box
              display="grid"
              gap="5"
              gridTemplateColumns="repeat(auto-fill, minmax(22rem,1fr))"
              gridAutoRows="max-content"
              mt="4"
            >
              <Loading opacity="0.75" fontSize="sm">Fetching uploaded scenes</Loading>
              { Array.from(Array(5)).map((a, ind) => 
              // eslint-disable-next-line react/no-array-index-key
                <LoadingSceneCard key={ind} />
              )}
            </Box>}
            { error && <Error mt="4">{ error.message }</Error> }
          </>
        ) : (
          <>
            {scenes?.features.length === 0 ? (
              <Box mt="4" display="flex" gap="2" flexDir="column" alignItems="flex-start">
                {defaultFilterState ?
                  <>
                    <Heading as="h4" size="sm">No scenes available</Heading>
                    <Text fontSize="sm">No scenes have been uploaded or processed. Click the &quot;Upload new scene&quot; button to get started.</Text>
                  </>
                  :
                  <>
                    <Heading as="h4" size="sm">No scenes match the current filters</Heading>
                    <Text fontSize="sm">Try changing or removing some of your filters, or adjusting the search area.</Text>
                    <Button
                      size="sm"
                      variant="base-soft-outline"
                      onClick={resetFilters}
                      leftIcon={<CollecticonArrowSemiSpinCcw />}
                    >
                    Reset Filters
                    </Button>
                  </>}
              </Box>
            ) : (
              <Box
                display="grid"
                gap="5"
                gridTemplateColumns="repeat(auto-fill, minmax(22rem,1fr))"
                gridAutoRows="max-content"
                mt="4"
                mb="8"
              >
                {scenesSorted?.map((scene: Feature<Geometry, SceneFeature>) => {
                  const sceneStats = inferenceStats[scene.properties.s3_prefix];
                  return (
                    <SceneCard
                      key={scene.properties.s3_prefix}
                      scene={scene}
                      inferencesLoading={inferenceLoading}
                      count={sceneStats ? sceneStats.count : 0}
                      validated={sceneStats ? sceneStats.validated : 0}
                      species={sceneStats ? sceneStats.species : {}}
                    />
                  );
                })}
              </Box>
            )}
          </>
        )}
        {view === ScenesView.Map &&
          <Box
            display="flex"
            bg={panelBg}
            width="100%"
            position="sticky"
            mt="-3.25rem"
            mb="-1.25rem"
            py={5}
            bottom={-5}
            left={0}
            right={0}
            flexDirection="column"
          >
            <DownloadDetections
              inferences={inferences}
              fileName={`all-detections-${new Date().toISOString().split(".")[0]}.csv`}
              isLoading={inferenceLoading}
              isDisabled={!inferences?.features.length}
            >
              Download All Inferences
            </DownloadDetections>
          </Box>}
      </Grid>
      {view === ScenesView.Map && (
        <Box display="grid" gridTemplateRows="1fr max-content">
          <Box position="relative">
            <Box
              position="absolute"
              top="4"
              left="4"
              zIndex="1000"
              borderRadius="md"
              padding="2"
              boxShadow="base"
              bgColor={checkboxBg}
              border="1px solid"
              borderColor="base.100a"
            >
              <Checkbox
                colorScheme="primary"
                isChecked={filterBbox}
                onChange={() => setFilterBbox((prev) => !prev)}
              >
                Filter scenes by the map
              </Checkbox>
            </Box>
            <Map
              ref={mapRef}
              onDragEnd={handleBboxChange}
              onZoomEnd={handleBboxChange}
            >
              <MapGrid data={scenes} />
              <MapScenesLegend />
            </Map>
          </Box>
          <Box py="8" px="8" pt="4">
            <DatePicker
              minDate={MIN_DATE}
              maxDate={MAX_DATE}
              selectedDates={selectedDates}
              setSelectedDates={setSelectedDates}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default Scenes;
